import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { RefreshCw } from 'lucide-react'
import { request } from '@/lib/request'
import { esQuery } from '@/lib/es'
import { usePersistentState } from '@/hooks/usePersistentState'

interface FlowItem {
  account: string
  balance: number
  cur: string
  bit: number
  current_pay: number
  code: string
  live_id: string
  pay_count: number
  current_count: number
  bet_down: number
  is_bet: boolean
  strategy: string
  date: string
  timestamp: number
}

export default function Flow() {
  const [data, setData] = useState<FlowItem[]>([])
  const [loading, setLoading] = useState(false)
  const [account, setAccount] = usePersistentState<string | undefined>(
    'admin:flow:account',
    undefined,
  )
  const [accounts, setAccounts] = useState<any[]>([])
  const [date, setDate] = usePersistentState('admin:flow:date', '10m')

  const loadTasks = async () => {
    try {
      const resp = await request('/system/api/query/task', {
        page_size: 100,
        page_num: 1,
        query_filter: {}
      })
      if (resp && resp.code === 0) {
        setAccounts(resp.data.list || [])
      }
    } catch (error) {
      console.error('Failed to load tasks:', error)
    }
  }

  const getData = async () => {
    setLoading(true)
    try {
      const body: any = {
        query: {
          bool: {
            filter: [],
            must: [
              {
                range: {
                  timestamp: {
                    gte: `now-${date}`,
                    lte: 'now',
                  },
                },
              },
            ],
          },
        },
        sort: [
          { timestamp: { order: 'desc' } },
        ],
      }

      if (account) {
        body.query.bool.filter.push({
          term: {
            'account.keyword': account,
          },
        })
      }

      const result = await esQuery('xr_pay_new', body)

      if (result) {
        const rs = result.data ? result.data : result
        const list = rs.hits.hits.map((val: any) => val._source)
        setData(list)
      }
    } catch (error) {
      console.error('Failed to fetch flow data:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadTasks()
  }, [])

  useEffect(() => {
    getData()
  }, [account])

  return (
    <div className="h-full p-6 overflow-auto">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">流程数据</h1>
          <Button variant="outline" size="sm" onClick={getData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            刷新
          </Button>
        </div>

        {/* Filters */}
        <div className="flex gap-3 items-center">
          <SearchableSelect
            className="w-48"
            value={account}
            onChange={setAccount}
            options={accounts.map((acc) => ({
              value: acc.account,
              label: acc.account,
            }))}
            placeholder="选择账户"
            searchPlaceholder="搜索账户..."
            emptyText="未找到账户"
            allowClear
          />

          <Input
            className="w-32"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            placeholder="10m"
          />
        </div>

        {/* Table */}
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>账户</TableHead>
                <TableHead>余额</TableHead>
                <TableHead>货币</TableHead>
                <TableHead>下注</TableHead>
                <TableHead>当前支付</TableHead>
                <TableHead>代码</TableHead>
                <TableHead>直播ID</TableHead>
                <TableHead>支付次数</TableHead>
                <TableHead>当前次数</TableHead>
                <TableHead>策略</TableHead>
                <TableHead>秒</TableHead>
                <TableHead>时间</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.length > 0 ? (
                data.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell className="font-medium">{item.account}</TableCell>
                    <TableCell>{item.balance}</TableCell>
                    <TableCell>{item.cur}</TableCell>
                    <TableCell>{item.bit}</TableCell>
                    <TableCell>{item.current_pay}</TableCell>
                    <TableCell>{item.code}</TableCell>
                    <TableCell>{item.live_id}</TableCell>
                    <TableCell>{item.pay_count}</TableCell>
                    <TableCell>{item.current_count}</TableCell>
                    <TableCell>{item.strategy}</TableCell>
                    <TableCell>{item.bet_down}</TableCell>
                    <TableCell className="text-xs">{item.date}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={11} className="text-center text-muted-foreground py-8">
                    {loading ? '加载中...' : '暂无数据'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        <div className="text-sm text-muted-foreground">共 {data.length} 条记录</div>
      </div>
    </div>
  )
}
